import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, } from '@tanstack/react-table';
import { useFilterState } from './useFilterState';
const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_SEARCH_DEBOUNCE_MS = 250;
/**
 * Page-level state hook for a server-paginated list page. Bundles together
 * `useFilterState`, `useReactTable`, debounced header search, fetch effect,
 * URL hydration + push, and pagination tracking.
 *
 * Modules use this hook to drive their JSX. They compose existing primitives
 * (Container, Card, FilterCardHeader, FilterBarRegistry, DataTable,
 * TablePagination) directly — there is no shell component.
 */
export function useListPage({ filters, initialValues, columns, fetcher, rowId, mode = 'server', clientFilter, lockedFilterValues, urlSync, defaultPageSize = DEFAULT_PAGE_SIZE, searchDebounceMs = DEFAULT_SEARCH_DEBOUNCE_MS, }) {
    const isServer = mode !== 'client';
    const history = useHistory();
    const location = useLocation();
    // Hydrate once on mount only; later URL changes are pushed, not pulled.
    const hydrated = useMemo(() => {
        if (urlSync)
            return urlSync.hydrate(location.search, initialValues);
        return {
            values: initialValues,
            page: 1,
            pageSize: defaultPageSize,
            search: '',
        };
    }, []);
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    // Clamp hydrated pagination — a malformed deep link (?page=0&pageSize=0)
    // must not bootstrap an invalid page (0 → fetcher) or pageIndex (-1 → table).
    const [page, setPage] = useState(Math.max(1, hydrated.page || 1));
    const [pageSize, setPageSize] = useState(Math.max(1, hydrated.pageSize || defaultPageSize));
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [rowSelection, setRowSelection] = useState({});
    const [sorting, setSorting] = useState([]);
    const [searchInput, setSearchInput] = useState(hydrated.search);
    const [debouncedSearch, setDebouncedSearch] = useState(hydrated.search);
    // Filter state seeded from URL hydration. reset() must clear back to the
    // *configured* defaults (not the deep-linked URL state), so keep the
    // original initialValues in a ref and re-apply them in handleReset below.
    const initialValuesRef = useRef(initialValues);
    const filterState = useFilterState(hydrated.values);
    const { values, displayed, labels, setValue, setMany, setLabel, toggle, reset: filterReset, } = filterState;
    // Debounced search ----------------------------------------------------
    useEffect(() => {
        const id = setTimeout(() => setDebouncedSearch(searchInput), searchDebounceMs);
        return () => clearTimeout(id);
    }, [searchInput, searchDebounceMs]);
    const mergedValues = useMemo(() => {
        const next = Object.assign({}, values);
        // FilterBarRegistry hides configs whose featureFlag() is false. Reset those
        // ids to their configured default before merging, so a hydrated/default
        // value for a now-hidden filter isn't silently forwarded to fetcher() /
        // clientFilter() as an active filter with no chip or editor to clear it.
        filters.forEach((cfg) => {
            if (cfg.featureFlag && !cfg.featureFlag()) {
                next[cfg.id] = initialValuesRef.current[cfg.id];
            }
        });
        return Object.assign(Object.assign({}, next), (lockedFilterValues || {}));
    }, [values, filters, lockedFilterValues]);
    // Fetch ---------------------------------------------------------------
    const aliveRef = useRef(true);
    // Monotonic request token: only the newest in-flight fetch may commit its
    // results, so an older response that resolves late can't clobber newer state.
    const requestSeqRef = useRef(0);
    // Active fetch controller — aborted when superseded or on unmount, so
    // fetchers that honor the signal can cancel in-flight requests.
    const abortRef = useRef(null);
    useEffect(() => () => {
        var _a;
        aliveRef.current = false;
        (_a = abortRef.current) === null || _a === void 0 ? void 0 : _a.abort();
    }, []);
    const [refetchNonce, setRefetchNonce] = useState(0);
    const refetch = useCallback(() => setRefetchNonce((n) => n + 1), []);
    const runFetch = useCallback(async (params) => {
        var _a;
        (_a = abortRef.current) === null || _a === void 0 ? void 0 : _a.abort(); // cancel any superseded in-flight fetch
        const seq = ++requestSeqRef.current;
        const controller = new AbortController();
        abortRef.current = controller;
        const isCurrent = () => aliveRef.current && seq === requestSeqRef.current;
        setIsLoading(true);
        try {
            const result = await fetcher(Object.assign(Object.assign({}, params), { signal: controller.signal }));
            if (!isCurrent())
                return;
            setRows(result.items || []);
            setTotalCount(result.totalCount || 0);
            setTotalPages(result.totalPages || 1);
        }
        catch (_b) {
            // Domain fetchers report their own errors; just clear loading state.
        }
        finally {
            if (isCurrent())
                setIsLoading(false);
        }
    }, [fetcher]);
    // Server mode: re-fetch one page whenever the query changes. When the
    // *query* (filters/search/sort) changes while on page > 1, reset to page 1
    // first and skip the fetch — the page change re-runs this effect, so only
    // one request fires (and never with an out-of-range stale page).
    const lastQueryRef = useRef({
        values: mergedValues,
        search: debouncedSearch,
        sorting,
    });
    useEffect(() => {
        if (!isServer)
            return;
        const last = lastQueryRef.current;
        const queryChanged = last.values !== mergedValues ||
            last.search !== debouncedSearch ||
            last.sorting !== sorting;
        lastQueryRef.current = {
            values: mergedValues,
            search: debouncedSearch,
            sorting,
        };
        if (queryChanged && page !== 1) {
            setPage(1);
            return;
        }
        runFetch({
            values: mergedValues,
            page,
            pageSize,
            search: debouncedSearch,
            sort: sorting,
        });
    }, [
        isServer,
        runFetch,
        mergedValues,
        page,
        pageSize,
        debouncedSearch,
        sorting,
        refetchNonce,
    ]);
    // Client mode: load the full dataset once (and on manual refetch). Chip
    // filters/sort/paging are applied in-memory, so they are NOT deps here —
    // only the role lock constrains what the server returns. The params still
    // honor the PagedFetchParams contract: a full default-shaped values object
    // (plus the role lock) and a fetch-all page size, so a fetcher that does
    // honor paging returns the whole dataset rather than one default page.
    useEffect(() => {
        if (isServer)
            return;
        runFetch({
            values: Object.assign(Object.assign({}, initialValuesRef.current), (lockedFilterValues || {})),
            page: 1,
            pageSize: Number.MAX_SAFE_INTEGER,
            search: '',
            sort: [],
        });
    }, [isServer, runFetch, lockedFilterValues, refetchNonce]);
    // Client-mode in-memory filtering.
    const filteredRows = useMemo(() => {
        if (isServer || !clientFilter)
            return rows;
        return rows.filter((row) => clientFilter(row, mergedValues, debouncedSearch));
    }, [isServer, clientFilter, rows, mergedValues, debouncedSearch]);
    const effectiveTotalCount = isServer ? totalCount : filteredRows.length;
    const effectiveTotalPages = isServer
        ? totalPages
        : Math.max(1, Math.ceil(filteredRows.length / pageSize));
    // Client mode: clamp the page when the in-memory result set shrinks below it
    // (a filter change or removing the last item on the final page), so
    // pagination never points past the end and renders an empty page. Only safe
    // in client mode — there effectiveTotalPages is derived synchronously from
    // filteredRows. In server mode totalPages updates a tick after the fetch, so
    // clamping would fight navigation (and reset a deep-linked ?page=N on mount).
    useEffect(() => {
        if (!isServer && page > effectiveTotalPages)
            setPage(effectiveTotalPages);
    }, [isServer, page, effectiveTotalPages]);
    // Client mode: reset page when filters/sort change (server mode handles
    // this inside the fetch effect). Skip the hydration tick so a deep-linked
    // ?page=3 isn't reset to 1.
    const filtersInitialized = useRef(false);
    useEffect(() => {
        if (!filtersInitialized.current) {
            filtersInitialized.current = true;
            return;
        }
        if (isServer)
            return;
        setPage(1);
    }, [isServer, values, debouncedSearch, sorting]);
    // Push state to URL (replace so back-button history isn't polluted) --
    useEffect(() => {
        if (!urlSync)
            return;
        // Serialize the sanitized query (mergedValues), NOT the raw values bag:
        // feature-flag-disabled filters are stripped from mergedValues before they
        // reach the fetcher, so serializing raw `values` would keep rewriting
        // stale hidden params into the URL even though they no longer apply.
        const search = urlSync.serialize(mergedValues, page, pageSize, debouncedSearch);
        const target = search
            ? `${location.pathname}?${search}`
            : location.pathname;
        if (`${location.pathname}${location.search}` === target)
            return;
        history.replace(target);
    }, [
        urlSync,
        mergedValues,
        page,
        pageSize,
        debouncedSearch,
        history,
        location.pathname,
        location.search,
    ]);
    // Tanstack table ------------------------------------------------------
    // Server mode is manual (the fetcher already filtered/sorted/paged one page).
    // Client mode lets the table sort + paginate the in-memory filtered rows.
    const onPaginationChange = useCallback((updater) => {
        const next = typeof updater === 'function'
            ? updater({ pageIndex: page - 1, pageSize })
            : updater;
        if (next.pageSize !== pageSize) {
            setPageSize(next.pageSize);
            setPage(1);
        }
        else {
            setPage(next.pageIndex + 1);
        }
    }, [page, pageSize]);
    const table = useReactTable({
        columns,
        data: isServer ? rows : filteredRows,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getRowId: rowId,
        manualPagination: isServer,
        manualSorting: isServer,
        pageCount: isServer ? totalPages : undefined,
        autoResetPageIndex: false,
        state: {
            rowSelection,
            sorting,
            pagination: { pageIndex: page - 1, pageSize },
        },
        onRowSelectionChange: setRowSelection,
        onSortingChange: setSorting,
        onPaginationChange,
        enableRowSelection: true,
    });
    // Selected rows derived from selection state -------------------------
    // Derived from the *current* dataset (not the table's row model) so a
    // refetch/filter swap can never surface row objects from a previous page.
    const selectedRows = useMemo(() => {
        const data = isServer ? rows : filteredRows;
        return data.filter((row) => rowSelection[rowId(row)]);
    }, [isServer, rows, filteredRows, rowSelection, rowId]);
    // Adapter handlers ----------------------------------------------------
    const handleSetValue = useCallback((id, value) => setValue(id, value), [setValue]);
    const handleSetLabel = useCallback((id, optionId, label) => setLabel(id, optionId, label), [setLabel]);
    const handleToggle = useCallback((id) => toggle(id), [toggle]);
    const handleReset = useCallback(() => {
        // filterReset restores displayed filters + clears labels, but its value
        // baseline is the URL-hydrated seed; snap values back to the configured
        // defaults so a deep-linked page fully clears.
        filterReset();
        setMany(initialValuesRef.current);
        // Clear both search states immediately — leaving debouncedSearch to expire
        // on its own fires one fetch with the stale term, then a second one.
        setSearchInput('');
        setDebouncedSearch('');
        setPage(1);
    }, [filterReset, setMany]);
    const handleSetPageSize = useCallback((size) => {
        setPageSize(size);
        setPage(1);
    }, []);
    return {
        // Filter state (with type-erased handlers for the FilterBarRegistry contract)
        values,
        displayed: displayed,
        labels: labels,
        setValue: handleSetValue,
        setLabel: handleSetLabel,
        toggle: handleToggle,
        reset: handleReset,
        // Table state
        table,
        selectedRows,
        // Pagination
        page,
        setPage,
        pageSize,
        setPageSize: handleSetPageSize,
        totalCount: effectiveTotalCount,
        totalPages: effectiveTotalPages,
        // Fetch
        rows,
        isLoading,
        refetch,
        // Search
        searchInput,
        setSearchInput,
        debouncedSearch,
        // Pass-through
        filters,
    };
}
